import React, { useId, useRef } from 'react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const options = [
  { value: 'manual', label: '100 üzerinden puan', icon: CalculateOutlinedIcon,
    description: 'Öğrencilere 0–100 arasında puan verirsiniz.' },
  { value: 'test', label: 'Test · Doğru sayısı', icon: FactCheckOutlinedIcon,
    description: 'Doğru sayısını girersiniz; puan, toplam soru sayısına göre hesaplanır.' },
  { value: 'development', label: 'Gelişim Düzeyi', icon: InsightsOutlinedIcon,
    description: 'Geliştirilmeli, Yeterli, İyi veya Çok İyi düzeylerinden birini seçersiniz.' },
];

export default function AssessmentScoringSelector({ value, onChange, disabled = false }) {
  const id = useId();
  const descriptionRef = useRef(null);
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const selectedOption = options.find(option => option.value === value) || options[0];

  return (
    <Box>
      <Typography id={labelId} variant="subtitle2" sx={{ mb: 1 }}>Puanlama türü</Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        disabled={disabled}
        aria-labelledby={labelId}
        onChange={(_, nextValue) => {
          if (nextValue) onChange(nextValue);
          requestAnimationFrame(() => descriptionRef.current?.scrollIntoView?.({ block: 'nearest' }));
        }}
        sx={{
          display: 'grid', gap: 1,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          '& .MuiToggleButtonGroup-grouped': {
            m: 0, border: '1px solid', borderColor: 'divider', borderRadius: '12px !important',
            minWidth: 0, minHeight: { xs: 60, sm: 100 }, px: 1.5, py: 1.25,
            display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, gap: 1,
            justifyContent: { xs: 'flex-start', sm: 'center' },
            color: 'text.primary', bgcolor: 'background.paper', textTransform: 'none',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
            '&.Mui-selected': {
              bgcolor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark', borderColor: 'primary.dark' },
            },
            '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'primary.dark', outlineOffset: 3 },
            '&.Mui-disabled': {
              color: 'text.disabled', bgcolor: 'action.disabledBackground', borderColor: 'divider',
              '&.Mui-selected': { borderColor: 'text.disabled' },
            },
          },
        }}
      >
        {options.map(({ value: optionValue, label, icon: Icon }) => {
          const selected = value === optionValue;
          const SelectionIcon = selected ? RadioButtonCheckedIcon : RadioButtonUncheckedIcon;
          return (
            <ToggleButton key={optionValue} value={optionValue} disableRipple
              aria-describedby={selected ? descriptionId : undefined}>
              <Icon sx={{ fontSize: 24, flexShrink: 0 }} />
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4,
                textAlign: { xs: 'left', sm: 'center' }, flexGrow: { xs: 1, sm: 0 } }}>{label}</Typography>
              <SelectionIcon sx={{ fontSize: 18, flexShrink: 0,
                position: { xs: 'static', sm: 'absolute' }, top: 10, right: 10 }} />
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      <Box ref={descriptionRef} role="status" aria-live="polite" aria-atomic="true" id={descriptionId}
        sx={{ mt: 1.5, p: 1.5, minHeight: { xs: 94, sm: 76 }, boxSizing: 'border-box',
          borderRadius: 2, bgcolor: theme => alpha(theme.palette.primary.main, 0.06) }}>
        <Typography component="p" sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
          {selectedOption.label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', fontSize: 13, lineHeight: 1.5 }}>
          {selectedOption.description}
        </Typography>
      </Box>
    </Box>
  );
}
